import { FCEvents } from '@fc/events';
import { patchDOM } from '@fc/dom-patch';
import { normalizeHTMLRoot } from '@fc/section-response';

const PRICE_DISPLAY_TAG = 'price-display';
const SECTION_SCOPE_SELECTOR = '.fit-section, .shopify-section, dialog, section';
const PRICE_CONTAINER_SELECTOR = '[data-ref="price-container"]';
const INSTALLMENTS_SELECTOR = '[data-ref="priceInstallments"]';
const PAYMENT_TERMS_VARIANT_SELECTOR = 'input[name="id"][data-ref="paymentTermsVariantId"]';

function escapeHTML(value) {
  const span = document.createElement('span');
  span.textContent = value == null ? '' : String(value);
  return span.innerHTML;
}

function escapeAttribute(value) {
  return escapeHTML(value).replace(/"/g, '&quot;');
}

class ProductPrice extends HTMLElement {
  #eventsController = null;
  #eventScope = null;
  #baseContainerHTML = null;
  #priceStatusFrame = null;

  connectedCallback() {
    this.#bindEvents();
    this.#snapshotBaseContainer();
    this.#ensurePriceLiveRegion();
  }

  disconnectedCallback() {
    this.#unbindEvents();
    this.#cancelPriceAnnouncement();
  }

  get productId() {
    return this.dataset.productId;
  }

  set productId(value) {
    if (value) {
      this.dataset.productId = String(value);
    } else {
      delete this.dataset.productId;
    }
  }

  #bindEvents() {
    this.#unbindEvents();
    this.#eventScope = this.closest(SECTION_SCOPE_SELECTOR) ?? document.body;
    this.#eventsController = new AbortController();

    this.#eventScope.addEventListener(FCEvents.variantChange, this.#onVariantChange, {
      signal: this.#eventsController.signal,
      passive: true,
    });

    this.#eventScope.addEventListener(FCEvents.sellingPlanChange, this.#onSellingPlanChange, {
      signal: this.#eventsController.signal,
      passive: true,
    });
  }

  #unbindEvents() {
    this.#eventsController?.abort();
    this.#eventsController = null;
    this.#eventScope = null;
  }

  /**
   * @param {Event & { detail?: { data?: { html?: string | Document | DocumentFragment | Element, productId?: string, newProduct?: { id?: string | number } } } }} event
   */
  #onVariantChange = (event) => {
    const eventData = event?.detail?.data;
    if (!eventData) return;

    if (eventData.newProduct?.id) {
      this.productId = String(eventData.newProduct.id);
    }

    const sourceProductId = this.#resolveSourceProductId(event, eventData);
    if (sourceProductId && this.productId && sourceProductId !== this.productId) return;

    const nextContainer = this.#resolveIncomingPriceContainer(eventData.html);
    const currentContainer = this.querySelector(PRICE_CONTAINER_SELECTOR);
    if (!(nextContainer instanceof Element) || !(currentContainer instanceof Element)) return;

    if (!currentContainer.isEqualNode(nextContainer)) {
      patchDOM(currentContainer, nextContainer);
    }
    this.#syncInstallments(eventData.html, event.detail.resource);
    this.#snapshotBaseContainer();
    this.#announceCurrentPrice();
  };

  #onSellingPlanChange = (event) => {
    const eventData = event?.detail;
    if (!eventData) return;

    const sourceProductId = this.#resolveSourceProductId(event, { productId: eventData.productId });
    if (sourceProductId && this.productId && sourceProductId !== this.productId) return;

    const currentContainer = this.querySelector(PRICE_CONTAINER_SELECTOR);
    if (!(currentContainer instanceof HTMLElement)) return;

    if (!eventData.pricing) {
      this.#restoreBaseContainer(currentContainer);
      this.#announceCurrentPrice();
      return;
    }

    this.#renderSellingPlanPrice(currentContainer, eventData.pricing, eventData.variant);
    this.#announceCurrentPrice();
  };

  /**
   * @param {Event} event
   * @param {{ productId?: string }} data
   * @returns {string | null}
   */
  #resolveSourceProductId(event, data) {
    if (event.target instanceof HTMLElement && event.target.dataset.productId) {
      return event.target.dataset.productId;
    }
    if (data.productId) return String(data.productId);
    return null;
  }

  /**
   * @param {string | Document | DocumentFragment | Element | undefined} payload
   * @returns {Element | null}
   */
  #resolveIncomingPriceContainer(payload) {
    if (!payload) return null;

    const sourceRoot = this.#normalizePayloadRoot(payload);
    if (!sourceRoot || typeof sourceRoot.querySelector !== 'function') return null;

    return sourceRoot.querySelector(`${PRICE_DISPLAY_TAG} ${PRICE_CONTAINER_SELECTOR}`);
  }

  /**
   * @param {string | Document | DocumentFragment | Element | undefined} payload
   * @param {{ id?: string | number } | null} variant
   */
  #syncInstallments(payload, variant) {
    const currentInstallments = this.querySelector(INSTALLMENTS_SELECTOR);
    const nextInstallments = this.#resolveIncomingInstallments(payload);

    if (!(nextInstallments instanceof Element)) {
      currentInstallments?.remove();
      return;
    }

    let targetInstallments = currentInstallments;
    if (targetInstallments instanceof Element) {
      patchDOM(targetInstallments, nextInstallments);
    } else {
      targetInstallments = this.#insertInstallments(nextInstallments);
    }

    this.#syncPaymentTermsVariantId(targetInstallments, this.#resolveVariantId(variant));
  }

  /**
   * @param {string | Document | DocumentFragment | Element | undefined} payload
   * @returns {Element | null}
   */
  #resolveIncomingInstallments(payload) {
    if (!payload) return null;

    const sourceRoot = this.#normalizePayloadRoot(payload);
    if (!sourceRoot || typeof sourceRoot.querySelector !== 'function') return null;

    return sourceRoot.querySelector(`${PRICE_DISPLAY_TAG} ${INSTALLMENTS_SELECTOR}`);
  }

  /**
   * @param {Element} nextInstallments
   * @returns {Element}
   */
  #insertInstallments(nextInstallments) {
    const clonedInstallments = nextInstallments.cloneNode(true);
    const anchor =
      this.querySelector('.price-tax-note') ??
      this.querySelector('[data-ref="priceLiveRegion"]') ??
      this.querySelector(PRICE_CONTAINER_SELECTOR);

    if (anchor instanceof Element) {
      anchor.after(clonedInstallments);
    } else {
      this.append(clonedInstallments);
    }

    return clonedInstallments;
  }

  /**
   * @param {Element | null} root
   * @param {string} variantId
   */
  #syncPaymentTermsVariantId(root, variantId) {
    const input = root?.querySelector?.(PAYMENT_TERMS_VARIANT_SELECTOR);
    if (!(input instanceof HTMLInputElement)) return;

    if (variantId) {
      input.value = variantId;
      input.setAttribute('value', variantId);
      return;
    }

    input.value = '';
    input.removeAttribute('value');
  }

  /**
   * @param {{ id?: string | number } | null | undefined} variant
   * @returns {string}
   */
  #resolveVariantId(variant) {
    // eslint-disable-next-line eqeqeq
    return variant?.id == null || variant.id === '' ? '' : String(variant.id);
  }

  /**
   * @param {string | Document | DocumentFragment | Element} payload
   * @returns {Document | DocumentFragment | Element | null}
   */
  #normalizePayloadRoot(payload) {
    return normalizeHTMLRoot(payload);
  }

  #snapshotBaseContainer() {
    const currentContainer = this.querySelector(PRICE_CONTAINER_SELECTOR);
    if (currentContainer instanceof HTMLElement) {
      this.#baseContainerHTML = currentContainer.outerHTML;
    }
  }

  #restoreBaseContainer(currentContainer) {
    if (!this.#baseContainerHTML) return;

    const baseContainer = this.#normalizePayloadRoot(this.#baseContainerHTML)?.querySelector(PRICE_CONTAINER_SELECTOR);
    if (baseContainer instanceof Element && !currentContainer.isEqualNode(baseContainer)) {
      patchDOM(currentContainer, baseContainer);
    }
  }

  #renderSellingPlanPrice(currentContainer, pricing, variant = null) {
    const price = Number.parseInt(String(pricing.price ?? ''), 10);
    const compareAtPrice = Number.parseInt(String(pricing.compareAtPrice ?? ''), 10);
    const priceFormatted = pricing.priceFormatted || '';
    const compareFormatted = pricing.compareAtPriceFormatted || '';
    if (!Number.isFinite(price) || !priceFormatted) return;

    const onSale = Number.isFinite(compareAtPrice) && compareAtPrice > price && compareFormatted;
    const saleFirst = currentContainer.dataset.salePriceFirst !== 'false';
    const currentPriceStyle = currentContainer.querySelector('.fc-price__current.price')?.getAttribute('style') || '';
    const comparePriceStyle = currentContainer.querySelector('.fc-price__original.compare-at-price')?.getAttribute('style') || currentPriceStyle;
    const unitPriceHTML = pricing.unitPriceHTML || '';
    const announcement = this.#buildPriceAnnouncement(currentContainer, pricing, variant, onSale);
    const announcementHTML = `<span class="visually-hidden" data-ref="priceAnnouncement">${escapeHTML(announcement)}</span>`;

    if (!onSale) {
      currentContainer.innerHTML = `<span class="fc-price__current price"${this.#styleAttribute(currentPriceStyle)}>${escapeHTML(priceFormatted)}</span>${unitPriceHTML}${announcementHTML}`;
      return;
    }

    const saleHTML = this.#priceGroup(
      currentContainer.dataset.saleLabel || 'Sale price',
      'fc-price__current price',
      priceFormatted,
      currentPriceStyle,
    );
    const compareHTML = this.#priceGroup(
      currentContainer.dataset.regularLabel || 'Regular price',
      'fc-price__original compare-at-price',
      compareFormatted,
      comparePriceStyle,
    );

    currentContainer.innerHTML = saleFirst
      ? `${saleHTML}${compareHTML}${unitPriceHTML}${announcementHTML}`
      : `${compareHTML}${saleHTML}${unitPriceHTML}${announcementHTML}`;
  }

  #styleAttribute(style) {
    return style ? ` style="${escapeAttribute(style)}"` : '';
  }

  #priceGroup(label, className, value, style) {
    return `<span role="group"><span class="visually-hidden">${escapeHTML(label)}&nbsp;</span><span class="${escapeAttribute(className)}"${this.#styleAttribute(style)}>${escapeHTML(value)}</span></span>`;
  }

  #buildPriceAnnouncement(currentContainer, pricing, variant, onSale) {
    const parts = [];
    const productTitle = currentContainer.dataset.productTitle?.trim();
    const variantTitle = currentContainer.dataset.variantTitle?.trim();
    const saleLabel = currentContainer.dataset.saleLabel || 'Sale price';
    const regularLabel = currentContainer.dataset.regularLabel || 'Regular price';

    if (productTitle) parts.push(productTitle);
    if (variantTitle && variantTitle !== productTitle) parts.push(variantTitle);

    if (pricing.sellingPlanName) parts.push(pricing.sellingPlanName);
    if (pricing.adjustmentLabel) parts.push(pricing.adjustmentLabel);

    if (onSale) {
      parts.push(`${saleLabel} ${pricing.priceFormatted}`);
      parts.push(`${regularLabel} ${pricing.compareAtPriceFormatted}`);
    } else {
      parts.push(`${regularLabel} ${pricing.priceFormatted}`);
    }

    if (
      pricing.sellingPlanId &&
      pricing.perDeliveryPriceFormatted &&
      pricing.perDeliveryPriceFormatted !== pricing.priceFormatted
    ) {
      const perDeliveryLabel = currentContainer.dataset.perDeliveryLabel || 'Per delivery';
      parts.push(`${perDeliveryLabel} ${pricing.perDeliveryPriceFormatted}`);
    }

    return parts.filter(Boolean).join('. ') + '.';
  }

  #announceCurrentPrice() {
    const message = this.#currentPriceAnnouncement();
    if (!message) return;
    this.#announcePrice(message);
  }

  #currentPriceAnnouncement() {
    const announcements = this.querySelectorAll('[data-ref="priceAnnouncement"]');
    const announcement = announcements[announcements.length - 1] ?? null;
    return announcement instanceof HTMLElement ? announcement.textContent?.trim() || '' : '';
  }

  #ensurePriceLiveRegion() {
    const existing = this.querySelector('[data-ref="priceLiveRegion"]');
    if (existing instanceof HTMLElement) return existing;

    const region = document.createElement('span');
    region.className = 'visually-hidden';
    region.dataset.ref = 'priceLiveRegion';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    this.append(region);
    return region;
  }

  #announcePrice(message) {
    const region = this.#ensurePriceLiveRegion();
    this.#cancelPriceAnnouncement();
    region.textContent = '';

    this.#priceStatusFrame = requestAnimationFrame(() => {
      this.#priceStatusFrame = null;
      if (this.isConnected) {
        region.textContent = message;
      }
    });
  }

  #cancelPriceAnnouncement() {
    if (this.#priceStatusFrame == null) return;
    cancelAnimationFrame(this.#priceStatusFrame);
    this.#priceStatusFrame = null;
  }
}

if (!customElements.get(PRICE_DISPLAY_TAG)) {
  customElements.define(PRICE_DISPLAY_TAG, ProductPrice);
}

export default ProductPrice;
